import { useEffect, useState } from 'react'
import type { ReactNode } from 'react'
import type { FieldKey } from '@esign/contracts'
import { Alert, BankCard, Button, Card, Field, SecurityNote, MailIcon, PhoneIcon, UserIcon, IdCardIcon, CalendarIcon, MapPinIcon, TaxIcon } from '@esign/ui'
import { FIELD_LABELS, errorText, initialValues } from '../fields.js'
import type { StepProps } from '../types.js'

const idCardFront = new URL('../assets/id-card-front.png', import.meta.url).href
const idCardBack = new URL('../assets/id-card-back.png', import.meta.url).href

function orientHorizontal(src: string): Promise<string> {
  return new Promise((resolve) => {
    if (typeof window === 'undefined' || !src || (!src.startsWith('http') && !src.startsWith('data:'))) {
      resolve(src)
      return
    }
    const img = new Image()
    img.crossOrigin = 'anonymous'
    img.onload = () => {
      if (img.naturalHeight > img.naturalWidth) {
        try {
          const canvas = document.createElement('canvas')
          canvas.width = img.naturalHeight
          canvas.height = img.naturalWidth
          const ctx = canvas.getContext('2d')
          if (ctx) {
            ctx.translate(canvas.width / 2, canvas.height / 2)
            ctx.rotate((270 * Math.PI) / 180)
            ctx.drawImage(img, -img.naturalWidth / 2, -img.naturalHeight / 2)
            resolve(canvas.toDataURL('image/jpeg', 0.95))
            return
          }
        } catch {
          // ignore
        }
      }
      resolve(src)
    }
    img.onerror = () => resolve(src)
    img.src = src
  })
}

const FIELD_ICONS: Partial<Record<FieldKey, ReactNode>> = {
  full_name: <UserIcon />,
  id_no: <IdCardIcon />,
  dob: <CalendarIcon />,
  id_issued_date: <CalendarIcon />,
  id_issued_place: <MapPinIcon />,
  tax_code: <TaxIcon />,
  address: <MapPinIcon />,
  phone: <PhoneIcon />,
  email: <MailIcon />,
}

const IDENTITY_FIELD_KEYS: FieldKey[] = ['full_name', 'id_no', 'dob', 'id_issued_date', 'id_issued_place', 'address']

export function ReviewStep({ step, context, error, busy, submit }: StepProps<'review'>) {
  const [values] = useState<Record<string, string>>(() => initialValues(context.subject))

  const identityFields = step.config.fields.filter(
    (field) => field !== 'id_images' && field !== 'bank_account' && IDENTITY_FIELD_KEYS.includes(field),
  )
  const basicFields = step.config.fields.filter(
    (field) => field !== 'id_images' && field !== 'bank_account' && !IDENTITY_FIELD_KEYS.includes(field),
  )
  const hasBankAccount = step.config.fields.includes('bank_account')
  const hasIdImages = step.config.fields.includes('id_images')

  const bankName = context.partner.name
  const holderName = String(context.subject['full_name'] ?? '')
  const accountNumber = maskAccount(String(context.subject['bank_account'] ?? ''))
  const frontImage = String(
    context.subject['front_image_url'] ||
      context.subject['front_image'] ||
      context.subject['frontSide'] ||
      (Array.isArray(context.subject['document_images']) ? context.subject['document_images'][0] : '') ||
      idCardFront,
  )
  const backImage = String(
    context.subject['back_image_url'] ||
      context.subject['back_image'] ||
      context.subject['backSide'] ||
      (Array.isArray(context.subject['document_images']) ? context.subject['document_images'][1] : '') ||
      idCardBack,
  )

  const [displayFront, setDisplayFront] = useState(frontImage)
  const [displayBack, setDisplayBack] = useState(backImage)

  useEffect(() => {
    orientHorizontal(frontImage).then(setDisplayFront)
  }, [frontImage])

  useEffect(() => {
    orientHorizontal(backImage).then(setDisplayBack)
  }, [backImage])

  return (
    <div className="review-step-container">
      {error && <Alert>{errorText(error.code, error.data)}</Alert>}

      {/* Card 1: Thông tin định danh */}
      <Card title="Thông tin định danh">
        {identityFields.map((name) => (
          <Field
            key={name}
            name={name}
            label={FIELD_LABELS[name]}
            value={values[name] ?? ''}
            icon={FIELD_ICONS[name]}
            readOnly
          />
        ))}

        {hasIdImages && <div className="cccd-section">
          <span className="field__label">Ảnh CCCD</span>
          <div className="cccd-grid">
            <div className="cccd-card">
              <span className="cccd-card__label">Mặt trước</span>
              <div className="cccd-card__img-wrapper">
                <img
                  className="cccd-card__image"
                  src={displayFront}
                  alt="Mặt trước căn cước công dân"
                />
              </div>
            </div>
            <div className="cccd-card">
              <span className="cccd-card__label">Mặt sau</span>
              <div className="cccd-card__img-wrapper">
                <img
                  className="cccd-card__image"
                  src={displayBack}
                  alt="Mặt sau căn cước công dân"
                />
              </div>
            </div>
          </div>
        </div>}
      </Card>

      {/* Card 2: Thông tin thanh toán */}
      {hasBankAccount && <Card title="Thông tin thanh toán">
        <BankCard
          bankName={bankName}
          holderName={holderName}
          accountNumber={accountNumber}
        />
      </Card>}

      {/* Card 3: Thông tin cơ bản */}
      {basicFields.length > 0 && <Card title="Thông tin cơ bản">
        {basicFields.map((name) => (
          <Field
            key={name}
            name={name}
            label={FIELD_LABELS[name]}
            value={values[name] ?? ''}
            icon={FIELD_ICONS[name]}
            readOnly
          />
        ))}
      </Card>}

      <SecurityNote />

      <Button aria-label="Ký hợp đồng" disabled={busy} onClick={() => submit(values)}>
        {busy ? 'Đang ký hợp đồng…' : 'Ký hợp đồng ngay'}
      </Button>
    </div>
  )
}

const maskAccount = (value: string): string =>
  value.length > 4 ? `${'*'.repeat(Math.min(8, value.length - 4))}${value.slice(-4)}` : value
